import { Archive, CheckCircle, WarningCircle } from "@phosphor-icons/react"
import { Badge } from "@tokenterminal/core/ui/messaging/badge/Badge"
import { Table } from "@tokenterminal/core/ui/table/Table"
import { useChainsList } from "modules/projects/hooks/useChainsList"
import Image from "next/image"
import Link from "next/link"
import { useMemo } from "react"
import { type ChainStatusItem } from "../../types/ApiTypes"
import { formatTimestampUTCDate } from "../../utils/date"
import { useProjectsArchiveStatus } from "./hooks/useProjectsArchiveStatus"

interface ChainStatusTableProps {
  chains: ChainStatusItem[]
}

export function ChainStatusTable({ chains }: ChainStatusTableProps) {
  const { data: { data: chainsMetadata = [] } = {} } = useChainsList()
  const { data: archiveStatusMap } = useProjectsArchiveStatus()

  // Create a mapping from chain IDs to their metadata
  const chainMetaMap = useMemo(() => {
    const map = new Map()
    chainsMetadata.forEach((chain) => {
      // For chains with multiple entries (like ethereum, polkadot), prioritize the main chain entry
      // where chain_id matches project_slug over specific product entries
      if (chain.project_slug && chain.chain_id === chain.project_slug) {
        // This is the main chain entry, always use it
        map.set(chain.project_slug, chain)
      } else if (!map.has(chain.project_slug)) {
        // This is a product/sub-chain entry, only use it if we don't have a main entry yet
        map.set(chain.project_slug, chain)
      }
      // Skip product entries if we already have the main chain entry
    })
    return map
  }, [chainsMetadata])

  if (chains.length === 0) {
    return (
      <div className="text-center py-8 text-fg-secondary">No chains found</div>
    )
  }

  return (
    <Table
      density="medium"
      rowHover={true}
      rowStyle="border"
      className="w-full"
    >
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Chain</Table.HeaderCell>
          <Table.HeaderCell>Status</Table.HeaderCell>
          <Table.HeaderCell className="text-right">
            Latest data
          </Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {chains.map((chain) => {
          const metadata = chainMetaMap.get(chain.slug)
          const chainName = metadata?.name || chain.slug
          const chainLogo = metadata?.logo
          const archiveStatus = archiveStatusMap?.get(chain.slug)
          const isArchived = archiveStatus?.isArchived || false

          // Determine status display
          let statusColor: "green" | "yellow" | "neutral" = "green"
          let statusIcon = <CheckCircle size={12} />
          let statusText = "Fresh"

          if (isArchived) {
            statusColor = "neutral"
            statusIcon = <Archive size={12} />
            statusText = "Archived"
          } else if (!chain.is_fresh) {
            statusColor = "yellow"
            statusIcon = <WarningCircle size={12} />
            statusText = "Stale"
          }

          return (
            <Table.Row key={chain.api_id}>
              <Table.Cell>
                <Link href={`/projects/${chain.slug}`} key={chain.api_id}>
                  <div className="flex items-center gap-3">
                    {chainLogo && (
                      <Image
                        src={`https://static1.tokenterminal.com/${chainLogo}`}
                        alt={chainName}
                        width={24}
                        height={24}
                        className="flex-shrink-0 rounded-full"
                      />
                    )}
                    <div className="flex flex-col">
                      <span className="font-medium text-fg-primary">
                        {chainName}
                      </span>
                    </div>
                  </div>
                </Link>
              </Table.Cell>
              <Table.Cell>
                <Link href={`/projects/${chain.slug}`} key={chain.api_id}>
                  <Badge
                    color={statusColor}
                    variant="muted"
                    className="inline-flex items-center gap-1"
                  >
                    {statusIcon}
                    {statusText}
                  </Badge>
                </Link>
              </Table.Cell>
              <Table.Cell className="text-right">
                <Link href={`/projects/${chain.slug}`} key={chain.api_id}>
                  <div className="flex flex-col items-end">
                    <span className="text-sm text-fg-primary">
                      {(() => {
                        const timestamp =
                          isArchived && archiveStatus?.archivedAt
                            ? archiveStatus.archivedAt
                            : chain.last_approved_timestamp
                        return formatTimestampUTCDate(timestamp)
                      })()}
                    </span>
                  </div>
                </Link>
              </Table.Cell>
            </Table.Row>
          )
        })}
      </Table.Body>
    </Table>
  )
}
